import { protocols } from "./constants";

export const validateApplicationProtocol = (protocols, protocolId) => {
  const protocolIdsSet = new Set(Object.values(protocols).map((protocol) => protocol.id));
  return protocolIdsSet.has(Number(protocolId));
};

export const isOnlyProtocolWithScore = (protocolId) => {
  const basicProtocols = Object.values(protocols.app.basic);
  const tensorFlowProtocols = Object.values(protocols.app.rotation);
  const arProtocols = Object.values(protocols.app.ar);
  const handTrackingProtocols = Object.values(protocols.web.handTracking);
  const fullBodyProtocols = Object.values(protocols.web.fullBody);
  const allProtocols = basicProtocols.concat(tensorFlowProtocols, arProtocols, handTrackingProtocols, fullBodyProtocols);

  const protocolsMapped = [
    ...allProtocols.map((protocol) => protocol.id),
    protocols.clinicAndHome.BUBBLES.id,
    protocols.clinicAndHome.DEMOLITION.id,
    protocols.clinicAndHome.CATERING.id,
  ];
  const allProtocolsId = new Set(protocolsMapped);
  return allProtocolsId.has(Number(protocolId));
};

export const isAppProtocol = (protocolId) => {
  return (
    validateApplicationProtocol(protocols.app.basic, protocolId) ||
    validateApplicationProtocol(protocols.app.rotation, protocolId) ||
    validateApplicationProtocol(protocols.app.ar, protocolId)
  );
};

export const isAppOrPlusProtocol = (protocolId) => {
  return (
    validateApplicationProtocol(protocols.app.basic, protocolId) ||
    validateApplicationProtocol(protocols.app.rotation, protocolId) ||
    validateApplicationProtocol(protocols.app.ar, protocolId) ||
    validateApplicationProtocol(protocols.web.handTracking, protocolId) ||
    validateApplicationProtocol(protocols.web.fullBody, protocolId)
  );
};

export const isProtocolWithSupport = (protocolId) => {
  const protocolsWithSupport = new Set([
    protocols.app.ar.SPHEROIDS.id,
    protocols.app.ar.HOCKEY.id,
    protocols.app.ar.WHAC_A_MOLE.id,
    protocols.app.ar.CLEANING_AR.id,
    protocols.app.ar.CIRCLE_HORIZONTAL.id,
    protocols.app.ar.QUALITY_CONTROL.id,
  ]);

  return protocolsWithSupport.has(Number(protocolId));
};

export const isProtocolWithoutHitsAndErrors = (protocolId) => {
  const protocolWithoutHitsAndErrors = new Set([
    protocols.app.basic.CLEANING.id,
    protocols.app.ar.ALPHABET.id,
    protocols.app.ar.TUBES.id,
    protocols.app.ar.CLEANING_AR.id,
    protocols.app.ar.CLEANING_AR_VERTICAL.id,
    protocols.app.ar.CIRCLE_HORIZONTAL.id,
  ]);

  return protocolWithoutHitsAndErrors.has(Number(protocolId));
};

export const isProtocolWithoutDifficultyModulators = (protocolId) => {
  const protocolWithoutDifficultyModulators = new Set([
    protocols.app.basic.CLEANING.id,
    protocols.app.ar.ALPHABET.id,
    protocols.app.ar.CLEANING_AR.id,
    protocols.app.ar.CLEANING_AR_VERTICAL.id,
    protocols.app.ar.CIRCLE_HORIZONTAL.id,
    protocols.web.handTracking.MIRROR.id,
    protocols.web.fullBody.TWISTER_BUDDY.id,
    protocols.web.fullBody.COSTUME_PARTY.id,
    protocols.web.fullBody.GENTLE_GIANT.id,
  ]);

  return protocolWithoutDifficultyModulators.has(Number(protocolId));
};

export const getProtocolTitle = (localization, protocolId) => {
  const protocolMapping = {
    // Clinic and Home
    [protocols.clinicAndHome.SPHEROIDS.id]: "spheroids",
    [protocols.clinicAndHome.PINBALL.id]: "pinball",
    [protocols.clinicAndHome.HOCKEY.id]: "hockey",
    [protocols.clinicAndHome.CLEAN_THE_TABLE.id]: "cleanTheTable",
    [protocols.clinicAndHome.CONSTELLATIONS.id]: "constellations",
    [protocols.clinicAndHome.GRASP_AND_PLACE.id]: "graspAndPlace",
    [protocols.clinicAndHome.FOLLOW_THE_LINE.id]: "followTheLine",
    [protocols.clinicAndHome.ROAD_TRIP.id]: "roadTrip",
    [protocols.clinicAndHome.BRICKS.id]: "bricks",
    [protocols.clinicAndHome.BOAT.id]: "boat",
    [protocols.clinicAndHome.BUBBLES.id]: "bubbles",
    [protocols.clinicAndHome.DEMOLITION.id]: "demolition",
    [protocols.clinicAndHome.CATERING.id]: "catering",
    // App: Basics
    [protocols.app.basic.MEMOSEQ.id]: "memoseqApp",
    [protocols.app.basic.PLACE_IT.id]: "placeIt",
    [protocols.app.basic.GUESS_WHAT.id]: "guessWhat",
    [protocols.app.basic.PIANOSEQ.id]: "pianoseq",
    [protocols.app.basic.BLOBS.id]: "blobs",
    [protocols.app.basic.SHOPPING.id]: "shopping",
    [protocols.app.basic.POPURRI.id]: "potpourri",
    [protocols.app.basic.TWISTER.id]: "twister",
    [protocols.app.basic.MEMORY.id]: "memory",
    [protocols.app.basic.CLEANING.id]: "cleaning",
    [protocols.app.basic.CONSTELLATIONS.id]: "constellationsApp",
    [protocols.app.basic.BALLOONS_BASIC.id]: "balloonsBasic",
    [protocols.app.basic.QUALITY_CONTROL_BASIC.id]: "qualityControlBasic",
    // App: Rotation
    [protocols.app.rotation.ARMANOID.id]: "armanoid",
    [protocols.app.rotation.DRIVING.id]: "driving",
    // App: AR
    [protocols.app.ar.TRAINING.id]: "training",
    [protocols.app.ar.SHELVES.id]: "shelves",
    [protocols.app.ar.BUFFET.id]: "buffet",
    [protocols.app.ar.ALPHABET.id]: "alphabet",
    [protocols.app.ar.BALLOONS.id]: "balloons",
    [protocols.app.ar.DUCKS.id]: "ducks",
    [protocols.app.ar.SPHEROIDS.id]: "spheroidsAR",
    [protocols.app.ar.HOCKEY.id]: "hockeyAR",
    [protocols.app.ar.CIRCLE.id]: "circle",
    [protocols.app.ar.WHAC_A_MOLE.id]: "whackAMoleAR",
    [protocols.app.ar.TUBES.id]: "tubes",
    [protocols.app.ar.CLEANING_AR.id]: "cleaningAR",
    [protocols.app.ar.CLEANING_AR_VERTICAL.id]: "cleaningARVertical",
    [protocols.app.ar.CIRCLE_HORIZONTAL.id]: "circleHorizontal",
    [protocols.app.ar.QUALITY_CONTROL.id]: "qualityControl",
    [protocols.app.ar.CONSTELLATIONS_AR.id]: "constellationsAR",
    // Web: Hand tracking
    [protocols.web.handTracking.MEMOSEQ.id]: "memoseqWeb",
    [protocols.web.handTracking.MIRROR.id]: "mirror",
    [protocols.web.handTracking.RECYCLING.id]: "recycling",
    [protocols.web.handTracking.WHAC_A_MOLE.id]: "whackAMole",
    [protocols.web.handTracking.FULL_BODY.id]: "Full body",
    [protocols.web.handTracking.BALANCE.id]: "balance",
    [protocols.web.handTracking.SEQUENCES.id]: "sequences",
    [protocols.web.handTracking.WORDS.id]: "words",
    [protocols.web.handTracking.OBJECTS_RAIN.id]: "objectsRain",
    // Web: Full body
    [protocols.web.fullBody.TWISTER_BUDDY.id]: "twisterBuddy",
    [protocols.web.fullBody.COSTUME_PARTY.id]: "costumeParty",
    [protocols.web.fullBody.GENTLE_GIANT.id]: "gentleGiant",
    // Icu
    [protocols.icu.THE_GARDEN.id]: "garden",
    [protocols.icu.THE_BEACH.id]: "beach",
    [protocols.icu.THE_AQUARIUM.id]: "aquarium",
    [protocols.icu.THE_UNIVERSE.id]: "universe",
    [protocols.icu.THE_NUMBERS.id]: "numbers",
    [protocols.icu.THE_RIVER.id]: "river",
    [protocols.icu.THE_BUTTERFLIES.id]: "butterflies",
    [protocols.icu.WALK_AROUND_THE_ISLAND.id]: "island",
    [protocols.icu.SUNSET_AMONG_POPPIES.id]: "sunset",
    [protocols.icu.CALM_IN_THE_LAKE.id]: "lake",
    [protocols.icu.FLYING_OVER_THE_FIELDS.id]: "fields",
    [protocols.icu.RELAX_ON_THE_BEACH.id]: "relaxOnTheBeach",
    [protocols.icu.THE_SOUND_OF_WATER.id]: "soundOfWater",
    [protocols.icu.THE_SWAY_OF_THE_WAVES.id]: "waves",
  };

  return protocolMapping[Number(protocolId)] ? `${localization}_${protocolMapping[Number(protocolId)]}` : "";
};

// Prefix used to build the game-mode info/name translation keys
// (e.g. `${prefix}_${GAME_MODE.toLowerCase()}_info`). These prefixes follow the
// localization CSV naming, which differs from getProtocolTitle (camelCase, AR suffixes,
// memoseqWEB, WHAC_A_MOLE -> fishingDayAR, etc.), so they need their own mapping.
export const getProtocolInfoPrefix = (protocolId) => {
  const prefixMapping = {
    // App: Basics
    [protocols.app.basic.MEMOSEQ.id]: "memoseq",
    [protocols.app.basic.GUESS_WHAT.id]: "guessWhat",
    [protocols.app.basic.PLACE_IT.id]: "placeIt",
    [protocols.app.basic.BLOBS.id]: "blobs",
    [protocols.app.basic.SHOPPING.id]: "shopping",
    [protocols.app.basic.POPURRI.id]: "potpourri",
    [protocols.app.basic.TWISTER.id]: "twister",
    [protocols.app.basic.MEMORY.id]: "memory",
    [protocols.app.basic.BALLOONS_BASIC.id]: "balloons",
    // App: Rotation
    [protocols.app.rotation.ARMANOID.id]: "armanoid",
    [protocols.app.rotation.DRIVING.id]: "driving",
    // App: AR
    [protocols.app.ar.BALLOONS.id]: "balloonsAR",
    [protocols.app.ar.SHELVES.id]: "shelvesAR",
    [protocols.app.ar.DUCKS.id]: "ducksAR",
    [protocols.app.ar.BUFFET.id]: "buffetAR",
    [protocols.app.ar.SPHEROIDS.id]: "spheroidsAR",
    [protocols.app.ar.HOCKEY.id]: "hockeyAR",
    [protocols.app.ar.WHAC_A_MOLE.id]: "fishingDayAR",
    [protocols.app.ar.TUBES.id]: "tubesAR",
    [protocols.app.ar.QUALITY_CONTROL.id]: "qualityControlAR",
    [protocols.app.ar.CONSTELLATIONS_AR.id]: "constellationsAR",
    // Web: Hand tracking
    [protocols.web.handTracking.MEMOSEQ.id]: "memoseqWEB",
    [protocols.web.handTracking.RECYCLING.id]: "recycling",
    [protocols.web.handTracking.WHAC_A_MOLE.id]: "whacAMole",
    [protocols.web.handTracking.SEQUENCES.id]: "sequences",
    [protocols.web.handTracking.OBJECTS_RAIN.id]: "objectsRain",
  };

  return prefixMapping[Number(protocolId)] ?? null;
};

export const getValidProtocols = (protocolsByType, hospitalProtocols) => {
  const protocolIdsByType = protocolsByType.filter((protocol) => protocol.isActive).map((protocol) => protocol.id);
  return hospitalProtocols.filter((hospitalProtocol) => protocolIdsByType.includes(hospitalProtocol.PROTOCOL_ID));
};

export const diagnosisProtocols = [
  {
    id: protocols.app.basic.QUALITY_CONTROL_BASIC.id,
  },
  {
    id: protocols.app.basic.CONSTELLATIONS.id,
  },
  {
    id: protocols.app.ar.CIRCLE.id,
  },
  {
    id: protocols.app.ar.CIRCLE_HORIZONTAL.id,
  },
];

export const isDiagnosisProtocol = (protocolId) => {
  const diagnosisProtocolsIds = new Set(diagnosisProtocols.map((diagnosisProtocol) => diagnosisProtocol.id));
  return diagnosisProtocolsIds.has(Number(protocolId));
};

export const isProtocolWithControls = (protocolId) => {
  const protocolWithControls = [
    {
      id: protocols.web.fullBody.TWISTER_BUDDY.id,
    },
    {
      id: protocols.web.fullBody.COSTUME_PARTY.id,
    },
    {
      id: protocols.web.fullBody.GENTLE_GIANT.id,
    },
  ];
  const protocolIds = new Set(protocolWithControls.map((protocol) => protocol.id));
  return protocolIds.has(Number(protocolId));
};

export const isProtocolWithCompensationControls = (protocolId) => {
  const protocolWithCompensationControls = [
    {
      id: protocols.web.fullBody.TWISTER_BUDDY.id,
    },
    {
      id: protocols.web.fullBody.COSTUME_PARTY.id,
    },
    {
      id: protocols.web.fullBody.GENTLE_GIANT.id,
    },
  ];
  const protocolIds = new Set(protocolWithCompensationControls.map((protocol) => protocol.id));
  return protocolIds.has(Number(protocolId));
};

export const isMotorProtocol = (protocolId) => {
  const motorProtocols = [
    protocols.app.basic.BLOBS.id,
    protocols.app.basic.SHOPPING.id,
    protocols.app.basic.CLEANING.id,
    protocols.app.basic.TWISTER.id,
    protocols.app.basic.BALLOONS_BASIC.id,
    protocols.app.rotation.DRIVING.id,
    protocols.app.rotation.ARMANOID.id,
    protocols.app.ar.BALLOONS.id,
    protocols.app.ar.DUCKS.id,
    protocols.app.ar.HOCKEY.id,
    protocols.app.ar.SHELVES.id,
    protocols.app.ar.TUBES.id,
    protocols.app.ar.ALPHABET.id,
    protocols.app.ar.BUFFET.id,
    protocols.app.ar.SPHEROIDS.id,
    protocols.app.ar.WHAC_A_MOLE.id,
    protocols.app.ar.TRAINING.id,
    protocols.app.ar.CLEANING_AR.id,
    protocols.app.ar.CLEANING_AR_VERTICAL.id,
  ];
  const protocolIds = new Set(motorProtocols);
  return protocolIds.has(Number(protocolId));
};

export const isCognitiveProtocol = (protocolId) => {
  const cognitiveProtocols = [
    protocols.app.basic.MEMOSEQ.id,
    protocols.app.basic.GUESS_WHAT.id,
    protocols.app.basic.MEMORY.id,
    protocols.app.basic.PLACE_IT.id,
    protocols.app.basic.POPURRI.id,
    protocols.app.basic.PIANOSEQ.id,
    protocols.app.ar.BALLOONS.id,
    protocols.app.ar.DUCKS.id,
    protocols.app.ar.HOCKEY.id,
    protocols.app.ar.SHELVES.id,
    protocols.app.ar.TUBES.id,
  ];
  const protocolIds = new Set(cognitiveProtocols);
  return protocolIds.has(Number(protocolId));
};

export const isLanguageProtocol = (protocolId) => {
  const languageProtocols = [
    protocols.app.basic.MEMOSEQ.id,
    protocols.app.basic.GUESS_WHAT.id,
    protocols.app.basic.MEMORY.id,
    protocols.app.basic.PLACE_IT.id,
    protocols.app.basic.POPURRI.id,
    protocols.app.ar.ALPHABET.id,
  ];
  const protocolIds = new Set(languageProtocols);
  return protocolIds.has(Number(protocolId));
};

export const AISNProtocols = () => {
  const basicProtocols = [
    protocols.app.basic.MEMOSEQ.id,
    protocols.app.basic.GUESS_WHAT.id,
    protocols.app.basic.MEMORY.id,
    protocols.app.basic.PLACE_IT.id,
    protocols.app.basic.POPURRI.id,
    protocols.app.basic.PIANOSEQ.id,
    protocols.app.basic.BLOBS.id,
    protocols.app.basic.SHOPPING.id,
    protocols.app.basic.TWISTER.id,
    protocols.app.basic.BALLOONS_BASIC.id,
    protocols.app.rotation.DRIVING.id,
  ];
  const arProtocols = [
    protocols.app.ar.BALLOONS.id,
    protocols.app.ar.DUCKS.id,
    protocols.app.ar.HOCKEY.id,
    protocols.app.ar.SHELVES.id,
    protocols.app.ar.TUBES.id,
    protocols.app.ar.ALPHABET.id,
    protocols.app.ar.BUFFET.id,
    protocols.app.ar.SPHEROIDS.id,
    protocols.app.ar.WHAC_A_MOLE.id,
    protocols.app.ar.TRAINING.id,
    protocols.app.ar.CLEANING_AR.id,
    protocols.app.ar.CLEANING_AR_VERTICAL.id,
  ];
  return { basicProtocols, arProtocols };
};
